import { useState } from 'react'
import { DatePicker } from '@cutilei/ui'
import { DateTime } from 'luxon'
import layoutOf from 'utils/layoutStyle'

/*
  Campo de data, sobre o DatePicker do @cutilei/ui, com as props de antes do Design System. O valor
  entra e sai em ISO ('2026-10-29'), como os formulários e os filtros guardam:

  - entra uma string ISO ou um DateTime do luxon; no intervalo, um array de 2;
  - sai `toISODate()`, ou `[início, fim]` no intervalo, só com as duas pontas escolhidas;
  - limpar devolve `null`, ou `[null, null]` no intervalo.

  O calendário abre num Popover. Dentro de um modal do site, escolher um dia não fecha o modal, e
  o Esc fecha só o calendário. `minRangeDays` e `maxRangeDays` bloqueiam os dias que dariam um
  intervalo fora dos limites, em vez de mostrar um erro depois da escolha.

  Do `style`, só valem largura e margens: o visual (sombra, cantos) é o do Design System.
*/

// À meia-noite do dia, no fuso do navegador. Nunca `new Date('2026-10-29')`, que lê em UTC.
const toDate = value => {
  if (!value) return undefined
  const dateTime = DateTime.isDateTime(value) ? value : DateTime.fromISO(String(value))
  return dateTime.isValid ? dateTime.startOf('day').toJSDate() : undefined
}

const toISO = date => DateTime.fromJSDate(date).toISODate()

export default function DateInput({
  value,
  style = {},
  placeholder,
  disabled = false,
  selectRange = false,
  clearable = false,
  disabledDates,
  minRangeDays = null,
  maxRangeDays = null,
  id,
  name,
  label,
  'aria-label': ariaLabel,
  onChange = () => {}
}) {
  // O primeiro clique do intervalo ainda não vai para a tela, que só recebe as duas pontas.
  const [partialRange, setPartialRange] = useState(null)

  const common = {
    size: 'sm',
    iconPosition: 'start',
    clearable,
    disabled,
    id,
    name,
    // Sem uma <label> ligada pelo `id`, o nome do campo vem da label da tela ou do placeholder.
    'aria-label': ariaLabel ?? (id ? undefined : (
      typeof label === 'string' ? label : (placeholder ?? (selectRange ? 'Período' : 'Data'))
    )),
    isDateDisabled: disabledDates ? date => Boolean(disabledDates({ date, view: 'month' })) : undefined
  }

  if (selectRange) {
    const [start, end] = Array.isArray(value) && value.length === 2 ? value : []
    const from = toDate(start)
    const to = toDate(end)

    return (
      <div style={layoutOf(style)}>
        <DatePicker
          {...common}
          mode='range'
          placeholder={placeholder ?? 'dd/mm/aaaa – dd/mm/aaaa'}
          minRangeDays={minRangeDays ?? undefined}
          maxRangeDays={maxRangeDays ?? undefined}
          value={partialRange ?? (from && to ? { from, to } : undefined)}
          onOpenChange={open => { if (!open) setPartialRange(null) }}
          onValueChange={range => {
            if (!range) {
              setPartialRange(null)
              onChange([null, null])
            } else if (range.from && range.to) {
              setPartialRange(null)
              onChange([toISO(range.from), toISO(range.to)])
            } else {
              setPartialRange(range)
            }
          }}
        />
      </div>
    )
  }

  return (
    <div style={layoutOf(style)}>
      <DatePicker
        {...common}
        placeholder={placeholder ?? 'dd/mm/aaaa'}
        value={toDate(value)}
        onValueChange={date => onChange(date ? toISO(date) : null)}
      />
    </div>
  )
}
